import './SelectField.css'

import { FocusEvent, MouseEvent, useMemo, useRef, useState } from 'react'

import { useFocus } from '../hooks/useFocus'
import { cx } from '../utilities/cx'
import { fieldHoc } from './FieldHoc'
import { Icon, IconName } from './Icon'
import { Label } from './Label'
import { SelectMenu, SelectMenuItem, SelectMenuItemGroup } from './SelectMenu'

export type SelectFieldItem<V extends string = string> = SelectMenuItem<V>
export type SelectFieldItemGroup<V extends string = string> = SelectMenuItemGroup<V>

export type SelectFieldProps = {
  className?: string
  items?: SelectFieldItem[] | SelectFieldItemGroup[]
  value?: string
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  tabIndex?: number
  startIcon?: IconName
  endIcon?: IconName
  label?: string
  placeholder?: string
  onBlur?: () => void
  onFocus?: () => void
  onChange?: (value: string) => void
}

export const SelectField = fieldHoc<string, SelectFieldProps>((props) => {
  const {
    className,
    items: itemsOrItemGroups = [],
    value,
    searchable,
    clearable,
    disabled,
    tabIndex,
    size,
    label,
    placeholder,
    onBlur,
    onFocus,
    onChange,
    hasError,
  } = props

  const focus = useFocus()
  const targetRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isOpen, setOpen] = useState(false)

  const itemGroups = useMemo(
    () =>
      itemsOrItemGroups.length !== 0 && 'items' in itemsOrItemGroups[0]!
        ? (itemsOrItemGroups as SelectFieldItemGroup[])
        : ([{ items: itemsOrItemGroups }] as SelectFieldItemGroup[]),
    [itemsOrItemGroups],
  )

  const currentItem = useMemo((): SelectFieldItem | undefined => {
    for (const itemGroup of itemGroups) {
      for (const item of itemGroup.items) {
        if (item.value === value) return item
      }
    }
    return undefined
  }, [itemGroups, value])

  const handleFocus = () => {
    if (isFocused) return
    setIsFocused(true)
    onFocus?.()
  }

  const handleBlur = (event: FocusEvent) => {
    if (targetRef.current?.contains(event.relatedTarget as Node)) return
    setIsFocused(false)
    onBlur?.()
  }

  const clearValue = () => {
    onChange?.('')
    requestAnimationFrame(() => {
      if (!targetRef.current?.contains(document.activeElement)) {
        setIsFocused(false)
        onBlur?.()
      }
    })
  }

  const handleClear = (event: MouseEvent) => {
    event.stopPropagation()
    clearValue()
  }

  const frameClasses = cx('select-field__frame', {
    'select-field__frame--focused': isFocused,
    'select-field__frame--disabled': !!disabled,
    'select-field__frame--error': hasError,
  })

  const renderCurrentItem = () => {
    if (!currentItem) {
      return <div className="select-field__placeholder">{placeholder ?? '\u00A0'}</div>
    }
    const internalStartIcon = currentItem.icon ?? currentItem.startIcon
    return (
      <>
        {internalStartIcon && (
          <div className="select-field__start-icon">
            <Icon name={internalStartIcon} />
          </div>
        )}
        {(currentItem.text ?? currentItem.content) && (
          <>
            <div className="select-field__current-item-text">{currentItem.text}</div>
            {currentItem.content}
          </>
        )}
        {currentItem.endIcon && (
          <div className="select-field__end-icon">
            <Icon name={currentItem.endIcon} />
          </div>
        )}
      </>
    )
  }

  const renderTarget = () => (
    <div className={frameClasses}>
      <div className="select-field__current-item">{renderCurrentItem()}</div>
      {clearable && currentItem && (
        <div
          className="select-field__clear-button"
          ref={focus.ref({ onSelect: clearValue })}
          onClick={handleClear}
        >
          <div className="select-field__icon">
            <Icon name="circle-x" />
          </div>
        </div>
      )}
      <div
        className={cx('select-field__menu-icon', {
          'select-field__menu-icon--open': isOpen,
          'select-field__menu-icon--disabled': !!disabled,
        })}
      >
        <Icon name="chevron-down" />
      </div>
    </div>
  )

  return (
    <div className={cx('select-field', { 'select-field--small': size === 'small' }, className)}>
      <div
        className="select-field__target"
        ref={targetRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {label && <Label>{label}</Label>}
        <SelectMenu
          items={itemsOrItemGroups}
          target={renderTarget()}
          value={value}
          tabIndex={tabIndex}
          placement="bottom"
          disabled={disabled}
          searchable={searchable}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={onChange}
        />
      </div>
    </div>
  )
})
